import { metricKeys } from '@pulse/metrics';
import {
  AUDIENCE_METRICS,
  WIDGET_TYPES,
} from '@components/top-widget-panel/spi-panel/widgets/constants';
import { DEFAULT_WIDGETS, SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import calcShortcutInterval from '@utils/calcShortcutInterval';
import { AD_PLAYS, DATE_SHORTCUTS } from '@utils/constants';
import { ALL_TRAFFIC } from '@utils/filter';
import getGranularity from '@utils/granularity';

export const eiPresetKeys = {
  EXEC_OVERVIEW: 'exec-overview',
  EXPERIENCE_OVERVIEW: 'exp-overview',
  ENGAGEMENT_OVERVIEW: 'eng-overview',
  DEVICE_PERFORMANCE: 'device-performance',
  ERROR_ANALYSIS: 'error-analysis',
  CDN_PERFORMANCE: 'cdn-performance',
  ANALYZE_ALERTS: 'analyze-alerts',
  AD_REACH: 'ad-reach',
  AD_PERFORMANCE: 'ad-perf',
};

export const eiPresetTitles = {
  [eiPresetKeys.EXEC_OVERVIEW]: 'Executive Overview',
  [eiPresetKeys.EXPERIENCE_OVERVIEW]: 'Experience Overview',
  [eiPresetKeys.ENGAGEMENT_OVERVIEW]: 'Engagement Overview',
  [eiPresetKeys.DEVICE_PERFORMANCE]: 'Device Performance',
  [eiPresetKeys.ERROR_ANALYSIS]: 'Error Analysis',
  [eiPresetKeys.CDN_PERFORMANCE]: 'CDN Performance',
  [eiPresetKeys.ANALYZE_ALERTS]: 'Analyze Alerts',
  [eiPresetKeys.AD_REACH]: 'Ad Reach & Frequency',
  [eiPresetKeys.AD_PERFORMANCE]: 'Ad Performance',
};

const calIntervalObjFromShortcut = (shortcut) => {
  const [from, to] = calcShortcutInterval(shortcut);
  return {
    shortcut,
    from,
    to,
    granularity: getGranularity(from, to, true, false, shortcut),
  };
};

const getEIUsecases = ({ validateMetric, validateDimension }) => [
  {
    title: 'Executive Overview',
    key: eiPresetKeys.EXEC_OVERVIEW,
    description: 'Track Concurrent Plays, SPI, Playing Time and Ad Impressions.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.StreamingPerformanceIndex,
          metricKeys.IntervalMinutesPlayed,
          metricKeys.AdPlays,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension(['OBJECT_NAME', 'm3.dv.n', 'LIVE_VIDEO', 'GEO_COUNTRY']),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric([
                metricKeys.Plays,
                metricKeys.ConcurrentPlays,
                metricKeys.AdPlays,
              ]),
            },
          },
        ],
      },
    },
  },
  {
    title: 'Experience Overview',
    key: eiPresetKeys.EXPERIENCE_OVERVIEW,
    description: 'Analyze SPI and understand the top Experience Improvement areas.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.StreamingPerformanceIndex,
          metricKeys.VideoStartFailuresTech,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.LowBitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension(['OBJECT_NAME', 'm3.dv.n', 'LIVE_VIDEO', 'GEO_COUNTRY']),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
    },
  },
  {
    title: 'Engagement Overview',
    key: eiPresetKeys.ENGAGEMENT_OVERVIEW,
    description: 'Track Audience and Engagement.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.EndedPlays),
        metrics: validateMetric([
          metricKeys.EndedPlays,
          metricKeys.UniqueDevices,
          metricKeys.Minutes,
          metricKeys.MinutesPerEndedPlay,
          metricKeys.MinutesPerUniqueDevice,
          metricKeys.PercentageComplete,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension(['OBJECT_NAME', 'm3.dv.n', 'LIVE_VIDEO', 'GEO_COUNTRY']),
          globalOrderBy: validateMetric(metricKeys.Plays),
          dimensionMetrics: validateMetric([metricKeys.Plays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric([
                metricKeys.Plays,
                metricKeys.ConcurrentPlays,
                metricKeys.IntervalMinutesPlayed,
                metricKeys.AdPlays,
              ]),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },

  {
    title: 'Device Performance',
    key: eiPresetKeys.DEVICE_PERFORMANCE,
    description:
      'Select a Device from the Dimension list to understand its performance by app version, device models, operating system version...',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.StreamingPerformanceIndex,
          metricKeys.VideoStartFailuresTech,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.LowBitrate,
          metricKeys.VideoStartTime,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.Bitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'appVersion',
            'm3.dv.mrk',
            'm3.dv.os',
            'm3.dv.osv',
            'm3.dv.br',
            'm3.dv.brv',
          ]),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  {
    title: 'Error Analysis',
    key: eiPresetKeys.ERROR_ANALYSIS,
    description: 'Analyze Startup and Playback errors.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.Attempts),
        metrics: validateMetric([
          metricKeys.Attempts,
          metricKeys.VideoStartFailuresTech,
          metricKeys.VideoStartFailuresBusiness,
          metricKeys.EndedPlays,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.VideoPlaybackFailuresBusiness,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'appVersion',
            'LIVE_VIDEO',
            'GEO_COUNTRY',
          ]),
          dimensionMetrics: validateMetric([
            metricKeys.Attempts,
            metricKeys.VideoStartFailuresTech,
            metricKeys.VideoPlaybackFailuresTech,
            metricKeys.EndedPlays,
          ]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  {
    title: 'CDN Performance',
    key: eiPresetKeys.CDN_PERFORMANCE,
    description:
      'Select a CDN from the Dimension list to understand its performance across different Geo and ISP metadata...',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.ConnectionInducedRebufferingRatio),
        metrics: validateMetric([
          metricKeys.IntervalMinutesPlayed,
          metricKeys.ConcurrentPlays,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.VideoStartFailuresTech,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.VideoStartTime,
          metricKeys.Bitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'CDN',
            'GEO_ASN',
            'GEO_CITY',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.Plays),
          dimensionMetrics: validateMetric([metricKeys.Plays]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  {
    title: 'Analyze Alerts',
    key: eiPresetKeys.ANALYZE_ALERTS,
    description: 'All Alerts related Metrics and Default Dimensions',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.ConnectionInducedRebufferingRatio),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.VideoStartFailuresTech,
          metricKeys.ExitBeforeVideoStarts,
          metricKeys.VideoStartTime,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.BadSession,
          metricKeys.AdRebufferingRatio,
          metricKeys.AdVideoStartFailures,
          metricKeys.AdBitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        interval: calIntervalObjFromShortcut(DATE_SHORTCUTS.past7Days),
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'c3.player.name',
            'm3.dv.osf',
            'LIVE_VIDEO',
            'OBJECT_NAME',
            'CDN',
            'GEO_ISP',
            'streamingProtocol',
            'GEO_CITY',
          ]),
          dimensionMetrics: validateMetric([
            metricKeys.Attempts,
            metricKeys.ConcurrentPlays,
            metricKeys.VideoStartFailuresTech,
            metricKeys.ExitBeforeVideoStarts,
            metricKeys.VideoStartTime,
            metricKeys.ConnectionInducedRebufferingRatio,
            metricKeys.VideoPlaybackFailuresTech,
          ]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
        diagnostics: {
          aiAlert: true,
          manualAlert: true,
        },
      },
    },
  },

  // {
  //   title: 'Metric Filter Test PoC',
  //   key: 'mf-poc',
  //   description: "Experience Insights is powered by Conviva's high-scale, real-time, on-demand platform that provides fast data accessibility with detailed data granularity, one-click and combinatorial instant filtering, and real-time, continuous measurement of quality of experience and audience engagement",
  //   config: {
  //     metricsChartContainer: {
  //       metric: validateMetric(metricKeys.StreamingPerformanceIndex),
  //       metrics: validateMetric([
  //         metricKeys.ConcurrentPlays,
  //         metricKeys.StreamingPerformanceIndex,
  //         metricKeys.VideoStartFailuresTech,
  //         metricKeys.HighExitBeforeVideoStart,
  //         metricKeys.HighRebuffering,
  //         metricKeys.HighStartupTime,
  //         metricKeys.VideoPlaybackFailuresTech,
  //         metricKeys.LowBitrate,
  //       ]),
  //     },
  //     common: {
  //       appliedFilters: [ALL_TRAFFIC],
  //     },
  //     'module:trends': {
  //       base: {
  //         metricFilter: [
  //           {
  //             metric: "RebufferingRatio",
  //             rule: {
  //               type: "bound",
  //               dimension: "LifeRebufferingRatio",
  //               upper: 8.5,
  //               lower: 0.9
  //             },
  //             meta: {
  //               range: [
  //                 19,
  //                 26
  //               ],
  //               type: "bound"
  //             }
  //           }
  //         ],
  //         dimensions: validateDimension([
  //           "OBJECT_NAME",
  //           "m3.dv.n",
  //           "LIVE_VIDEO",
  //           "GEO_COUNTRY"
  //         ]),
  //         globalOrderBy: "EndedPlays",
  //         dimensionMetrics: validateMetric([
  //           "EndedPlays",
  //           "UniqueDevices"
  //         ])
  //       }
  //     }
  //   }
  // },
];

const getAIUsecasesForEI = ({ validateMetric, validateDimension }) => [
  {
    title: 'Ad Reach & Frequency',
    key: eiPresetKeys.AD_REACH,
    description:
      'Track Ad Frequency and Reach. Understand Ad Load and how for many Ad Impressions the Ad is watched completely. Track Ad Slates.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AdPlays),
        metrics: validateMetric([
          metricKeys.AdPlays,
          metricKeys.AdUniqueDevices,
          metricKeys.AdTotalMinutes,
          metricKeys.AdEndedPlaysPerUniqueDevice,
          metricKeys.AdCompletedCreativePlays,
          metricKeys.AdSlateDurationPct,
          metricKeys.AdSlatePlaysPct,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'AD_OBJECT_NAME',
            'm3.dv.n',
            'c3.ad.position',
            'LIVE_VIDEO',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.AdPlays),
          dimensionMetrics: validateMetric([metricKeys.AdPlays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric(AUDIENCE_METRICS),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },

  {
    title: 'Ad Performance',
    key: eiPresetKeys.AD_PERFORMANCE,
    description: 'Analyze Ad Performance and User Experience.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AdPlays),
        metrics: validateMetric([
          metricKeys.AdPlays,
          metricKeys.AdCompletedCreativePlays,
          metricKeys.AdBitrate,
          metricKeys.AdVideoStartFailures,
          metricKeys.AdVideoPlaybackFailures,
          metricKeys.AdRebufferingRatio,
          metricKeys.AdVideoStartTime,
          metricKeys.AdExitBeforeVideoStarts,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'AD_OBJECT_NAME',
            'm3.dv.n',
            'c3.ad.position',
            'c3.ad.technology',
          ]),
          globalOrderBy: validateMetric(metricKeys.AdPlays),
          dimensionMetrics: validateMetric([metricKeys.AdPlays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric(AUDIENCE_METRICS),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },
];

export const genEIUsecases = ({ validateMetric, validateDimension }) => {
  if (validateMetric([AD_PLAYS]).includes(AD_PLAYS)) {
    return [
      ...getEIUsecases({ validateMetric, validateDimension }),
      ...getAIUsecasesForEI({ validateMetric, validateDimension }),
    ];
  } else {
    return getEIUsecases({ validateMetric, validateDimension });
  }
};
